import _ from "lodash"
import {nanoid} from "nanoid"
import {deToNumberString} from "../content/widgets/bloom-utils/transformations"
import {debounce, getQueryParam} from "../misc/utils"
import {useRef} from "react"

const baseBerechnungsvorgaben = {
    versorgungsziel: 100,
    invest: 0,
    zeitraum: 10,
    zielvermoegen: 0,
    aktuellesVermoegen: 0,
    laufzeit: 0,
    zinssatz: 0,
    immobilienPreis: 0,
    immobilienErwerb: 0,
}

const mapPerson = (person) => ({
    ...person,
    jaehrlichesBruttoEinkommen: _.isString(person.jaehrlichesBruttoEinkommen)
        ? Number.parseFloat(deToNumberString(person.jaehrlichesBruttoEinkommen))
        : person.jaehrlichesBruttoEinkommen,
})

const toBottomLabels = (verlaufData) =>
    verlaufData?.map((row) => String(row.jahr)) ?? []

const datasetConfig = (theme) => [
    {
        label: "Netto Versorgung aus Einkünften",
        key: "nettoVersorgungAusEinkuenften",
        color: theme.VERSORGUNGSLUECKENVERLAUF?.einkuenfte ?? "#0B2447",
    },
    {
        label: "Altersrente",
        key: "gesetzlicheVersorgungNettoEuro",
        color: theme.VERSORGUNGSLUECKENVERLAUF?.rente ?? "#66A61E",
    },
    {
        label: "Bestehende Versorgung",
        key: "summeNettorentenPrivateUndBetrieblicheBestehendeVersorgungEuro",
        color:
            theme.VERSORGUNGSLUECKENVERLAUF?.bestehendeVersorgung ?? "#1F78B4",
    },
    {
        label: "Versorgungslücke",
        key: "luecke",
        color: theme.VERSORGUNGSLUECKENVERLAUF?.luecke ?? "#D62828",
    },
    {
        label: "Versorgungslücke Ehepartner",
        key: "lueckePartner",
        color: theme.VERSORGUNGSLUECKENVERLAUF?.lueckePartner ?? "#D95F02",
    },
]

const toDatasets = (verlaufData, theme) =>
    datasetConfig(theme).map(({label, key, color}) => ({
        label,
        backgroundColor: color,
        data: verlaufData?.map((row) => row[key]) ?? [],
    }))

const computeRentebeginn = (geburtsdatum, renteneintrittsalter) => {
    if (!geburtsdatum || renteneintrittsalter == null) return null
    const birth = new Date(geburtsdatum)
    if (Number.isNaN(birth.getTime())) return null
    const year = birth.getFullYear() + Number(renteneintrittsalter)
    const mm = String(birth.getMonth() + 1).padStart(2, "0")
    const dd = String(birth.getDate()).padStart(2, "0")
    return `${year}-${mm}-${dd}`
}

const toWidgetData = (verlaufData, theme, person) => ({
    chart: {
        bottomLabels: toBottomLabels(verlaufData),
        datasets: toDatasets(verlaufData, theme),
    },
    events: [],
    rentebeginn: computeRentebeginn(
        person?.geburtsdatum,
        person?.renteneintrittsalter,
    ),
})

export const GesamtanalyseActions = ({
    sendDataToBackend,
    getDataFromPointer,
    updateData,
    getDataFromBackend,
    theme,
}) => {
    const doCalculation = () => {
        const kunde = getDataFromPointer("/versorgungsluecke/kundeNp")
        const ehepartner = getDataFromPointer("/versorgungsluecke/ehepartner")
        const berechnungsvorgaben = {
            ...baseBerechnungsvorgaben,
            ...getDataFromPointer("/berechnungsvorgabenVv"),
            ...getDataFromPointer("/berechnungsvorgabenGesamtanalyse"),
        }

        const payload = {
            kundeNp: {
                ...mapPerson(kunde),
                bestehendeVersorgung:
                    getDataFromPointer("/bestehendeVersorgung") ?? {},
            },
            ehepartner: {
                ...mapPerson(ehepartner),
                familienstand: kunde.familienstand,
            },
            berechnungsvorgaben: {
                ...berechnungsvorgaben,
                inflationssatzInProzent: Number(
                    berechnungsvorgaben.inflationssatzInProzent,
                ),
                zeitraum: Number(berechnungsvorgaben.zeitraum),
            },
        }

        return Promise.all([
            sendDataToBackend(
                getDataFromPointer("/links/calculateVersorgungslueckenVerlauf"),
                payload,
            ),
            sendDataToBackend(
                getDataFromPointer("/links/calculateVersorgungssituationAlter"),
                payload,
            ),
        ]).then(([resultVerlauf, resultTabelle]) => {
            if (!resultVerlauf) return
            const {_data: verlaufData} = resultVerlauf
            const {kunde: kundeVerlauf, ehepartner: ehepartnerVerlauf} =
                verlaufData

            const existingKunde = getDataFromPointer("/gesamtanalyse/kundeNp")
            const existingEhepartner = getDataFromPointer(
                "/gesamtanalyse/ehepartner",
            )
            updateData(
                {
                    ...toWidgetData(kundeVerlauf, theme, kunde),
                    events: existingKunde?.events ?? [],
                },
                "/gesamtanalyse/kundeNp",
            )
            updateData(
                {
                    ...toWidgetData(ehepartnerVerlauf, theme, ehepartner),
                    events: existingEhepartner?.events ?? [],
                },
                "/gesamtanalyse/ehepartner",
            )
            updateData(resultTabelle?._data ?? {}, "/gesamtanalyse/bilanz")
        })
    }

    const latestCalcRef = useRef()
    latestCalcRef.current = doCalculation

    const debouncedSendDataRef = useRef()
    if (!debouncedSendDataRef.current) {
        debouncedSendDataRef.current = debounce(
            (...args) => latestCalcRef.current(...args),
            500,
        )
    }

    return {
        INIT_GESAMTANALYSE: async () => {
            const initDataUrl = getQueryParam("initUrl")
            if (initDataUrl) {
                getDataFromBackend({href: initDataUrl, methods: ["GET"]}, "/")
                    .then((payload) =>
                        getDataFromBackend(
                            payload._links.loadPerson,
                            "/kundeNp",
                        ),
                    )
                    .then(doCalculation)
            } else {
                await doCalculation()
            }
        },
        CALCULATE_GESAMTANALYSE: debouncedSendDataRef.current,
        ADD_GESAMTANALYSE_EVENT: () => {
            const localData = getDataFromPointer("/appstate/localMilestone")
            const milestoneObj = getDataFromPointer(
                "/appstate/selectedMilestone",
            )
            const current = getDataFromPointer("/gesamtanalyse/kundeNp")
            const events = current?.events ?? []
            const exists = events.some((e) => e.id === localData?.id)
            const today = new Date()
            const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`
            const newEvents = exists
                ? [...events.filter((e) => e.id !== localData.id), localData]
                : [
                      ...events,
                      {
                          ...localData,
                          ...milestoneObj,
                          id: nanoid(),
                          date: localData?.date ?? todayStr,
                      },
                  ]
            updateData(
                {...current, events: newEvents},
                "/gesamtanalyse/kundeNp",
            )
            updateData({}, "/appstate/localMilestone")
            updateData(false, "/appstate/addMilestoneModalOpen")
        },
        GESAMTANALYSE_DRAG_END: (payload) => {
            const eventsList = getDataFromPointer(
                "/gesamtanalyse/kundeNp/events",
            )
            const newEventsList = [
                ...(eventsList.filter((e) => e.id !== payload.id) ?? []),
                payload,
            ]
            updateData(newEventsList, "/gesamtanalyse/kundeNp/events")
        },
    }
}
